import type { Participant } from '@kinop/shared';
import { formatTimecode } from '@kinop/shared';
import { UserAvatar, UserBadge } from './UserAvatar.js';

interface WatcherStripProps {
  participants: Participant[];
  myClientId: string | null;
  /** Комната стоит на общей паузе. */
  roomPaused: boolean;
}

type State = 'stalled' | 'ad' | 'local' | 'paused' | 'watching';

const LABELS: Record<State, string> = {
  stalled: 'нет связи',
  ad: 'реклама',
  local: 'своя пауза',
  paused: 'пауза',
  watching: 'смотрит',
};

/**
 * Что происходит у каждого.
 *
 * Порядок проверок — от того, что мешает сильнее. Человек, у которого не
 * грузится видео, стоит на паузе технически, но написать ему «пауза» значит
 * скрыть настоящую причину: он не отдыхает, у него не работает.
 */
function stateOf(participant: Participant, roomPaused: boolean): State {
  if (participant.isBuffering) return 'stalled';
  if (participant.adPlaying) return 'ad';
  if (participant.localPaused) return 'local';
  if (roomPaused) return 'paused';
  return 'watching';
}

/**
 * Полоса зрителей над чатом.
 *
 * Показывает не «кто в комнате» — это есть на вкладке «Люди», — а что у них
 * происходит прямо сейчас. Когда кино встало у одного, остальные должны
 * видеть причину, не переспрашивая в чате.
 */
export function WatcherStrip({
  participants,
  myClientId,
  roomPaused,
}: WatcherStripProps): JSX.Element | null {
  if (participants.length === 0) return null;

  return (
    <div className="watchers" aria-label="Кто что смотрит">
      {participants.map((participant) => {
        const state = stateOf(participant, roomPaused);
        const isMe = participant.clientId === myClientId;
        return (
          <div key={participant.clientId} className="watcher" data-state={state}>
            <span className="watcher__mark">
              <UserAvatar
                nickname={participant.nickname}
                avatar={participant.avatar}
                className="avatar--sm"
              />
              <span className="watcher__dot" aria-hidden="true" />
            </span>

            <span className="watcher__text">
              <span className="watcher__name">
                {isMe ? 'вы' : participant.nickname}
                <UserBadge badge={participant.avatar?.badge} />
              </span>
              <span className="watcher__status mono">
                {/* Время просмотра показываем только когда оно что-то значит:
                    у застрявшего и у того, кому крутят рекламу, оно врёт. */}
                {state === 'watching' || state === 'paused'
                  ? `${LABELS[state]} · ${formatTimecode(participant.positionMs)}`
                  : LABELS[state]}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
